import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, X, Loader2, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TipTapEditor } from '@/components/editor/TipTapEditor';
import { generateExcerpt } from '@/lib/tiptap';
import { useCategories } from '@/hooks/useKBData';
import { useAuth } from '@/providers/AuthProvider';
import { VersionHistoryDrawer } from '@/components/kb/VersionHistoryDrawer';
import { cn } from '@/lib/utils';

interface KBEditorProps {
  article?: any;
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
  isSaving?: boolean;
}

export function KBEditor({ article, onSave, onCancel, isSaving = false }: KBEditorProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState(article?.title || '');
  const [slug, setSlug] = useState(article?.slug || '');
  const [categoryId, setCategoryId] = useState<string>(article?.category_id?.toString() || '');
  const [content, setContent] = useState(() => {
    if (article?.content_json) {
      try {
        return JSON.parse(article.content_json);
      } catch (error) {
        console.warn('Failed to parse content_json, using as HTML:', error);
        return article.content_json;
      }
    }
    return article?.content || '';
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch categories for dropdown
  const { data: categoriesData } = useCategories();
  const categories = categoriesData?.categories || [];

  // Handle content changes from TipTap editor
  const handleContentUpdate = (newContent: string) => {
    setContent(newContent);
    setHasChanges(true);
  };

  // Auto-generate slug from title
  useEffect(() => {
    if (!article && title) {
      const generatedSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setSlug(generatedSlug);
    }
  }, [title, article]);

  // Track changes for any form input
  useEffect(() => {
    if (article) {
      const titleChanged = title !== article.title;
      const slugChanged = slug !== article.slug;
      const categoryChanged = categoryId !== article.category_id?.toString();
      const originalContent = (() => {
        if (article?.content_json) {
          try {
            return JSON.parse(article.content_json);
          } catch (error) {
            return article.content_json;
          }
        }
        return article?.content || '';
      })();
      const contentChanged = content !== originalContent;
      setHasChanges(titleChanged || slugChanged || categoryChanged || contentChanged);
    } else {
      setHasChanges(title.trim() !== '' || slug.trim() !== '' || categoryId !== '' || content.trim() !== '');
    }
  }, [title, slug, categoryId, content, article]);

  const handleSave = async () => {
    if (!content || !title.trim() || !slug.trim() || !categoryId || isNaN(parseInt(categoryId)) || !user?.id) {
      console.error('Save validation failed:', {
        hasContent: !!content,
        hasTitle: !!title.trim(),
        hasSlug: !!slug.trim(),
        hasCategoryId: !!categoryId,
        categoryIdValid: !isNaN(parseInt(categoryId)),
        hasUserId: !!user?.id
      });
      return;
    }

    // Generate excerpt from HTML content
    const excerpt = generateExcerpt(content);

    const saveData = {
      title: title.trim(),
      slug: slug.trim(),
      category_id: parseInt(categoryId),
      author_id: user.id,
      content: content, // This is HTML from TipTap editor.getHTML()
      content_json: content, // Store the same HTML for consistency
      content_format: 'tiptap',
      excerpt,
    };

    try {
      await onSave(saveData);
      setHasChanges(false);
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (!confirmed) return;
    }
    onCancel();
  };

  const canSave = title.trim() && slug.trim() && categoryId && user?.id && !isSaving;

  return (
    <div className="flex flex-col h-full">
      {/* Sticky Top Bar */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-2">
            <h1 className="text-lg font-semibold">
              {article ? 'Edit Article' : 'New Article'}
            </h1>
            {hasChanges && !isSaving && (
              <span className="text-xs text-muted-foreground bg-orange-500/10 text-orange-600 px-2 py-1 rounded">
                Unsaved changes
              </span>
            )}
            {isSaving && (
              <span className="text-xs text-muted-foreground bg-blue-500/10 text-blue-600 px-2 py-1 rounded flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving...
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {article?.slug && (
              <VersionHistoryDrawer
                articleSlug={article.slug}
                trigger={
                  <Button variant="ghost" size="sm">
                    <History className="h-4 w-4 mr-2" />
                    <span className="hidden sm:inline">History</span>
                  </Button>
                }
              />
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!canSave}
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* Editor Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
          {/* Article Metadata */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Article title..."
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      {category.icon} {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="slug">URL Slug</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="url-friendly-slug"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This will be the URL path for your article
              </p>
            </div>
          </div>

          {/* TipTap Editor */}
          <div>
            <Label>Content</Label>
            <div className="mt-1">
              <TipTapEditor
                content={content}
                onUpdate={handleContentUpdate}
                placeholder="Start writing your article..."
                className="focus-within:ring-2 focus-within:ring-ring"
                articleTitle={title || 'article'}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Type @ to mention users, use # for headings, - for lists, and [ ] for checklists
            </p>
          </div>

          {/* Mobile Bottom Actions */}
          <div className="md:hidden fixed bottom-4 left-4 right-4 z-50 flex justify-center">
            <div className="bg-background border border-border rounded-lg shadow-lg p-3 flex space-x-3">
              <Button
                variant="outline"
                size="default"
                onClick={handleCancel}
                onTouchStart={(e) => e.stopPropagation()}
                disabled={isSaving}
                className="min-h-[44px] px-6 touch-manipulation"
              >
                Cancel
              </Button>
              <Button
                size="default"
                onClick={handleSave}
                onTouchStart={(e) => e.stopPropagation()}
                disabled={!canSave}
                className="min-h-[44px] px-6 touch-manipulation"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}